/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    this.querySelectorAll('select[name="id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.handleOutOfStockOptions()
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    let selectedVariant = null
    try {
      const variants = JSON.parse(this.form.dataset.variants || '[]')
      selectedVariant = variants.find(variant => {
        if (variant.options && Array.isArray(variant.options)) {
          return JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
        }
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean)
        if (variantOptions.length > 0) {
          return JSON.stringify(variantOptions) === JSON.stringify(selectedOptions)
        }
        return variant.title === selectedOptions.join(' / ') || variant.title === selectedOptions[0]
      })
    } catch (e) {}

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)
    this.handleOutOfStockOptions()

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))

      this.fetchNewDocument(selectedVariant)
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (soldOutBadge) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant && selectedVariant.available) {
      this.atcBtn.removeAttribute('disabled')
      this.atcBtn.classList.remove('zena-demand-trigger')
      this.atcBtn.classList.remove('btn-sold-out')
      this.atcBtn.innerHTML = `
        <span class="pdp-atc-inner d-flex align-items-center justify-content-center gap-2">
          <svg class="pdp-atc-icon-bag" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
          <span class="pdp-atc-text">${window.theme?.locales?.product?.add_to_cart || 'ADD TO BAG'}</span>
          <svg class="pdp-atc-icon-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
        </span>`
      if (this.buyBtn) {
        this.buyBtn.removeAttribute('disabled')
        this.buyBtn.classList.remove('zena-demand-trigger')
        this.buyBtn.style.display = ''
      }
    } else {
      this.atcBtn.removeAttribute('disabled')
      this.atcBtn.classList.add('zena-demand-trigger')
      this.atcBtn.classList.add('btn-sold-out')
      this.atcBtn.innerHTML = `
        <span class="pdp-atc-inner d-flex align-items-center justify-content-center gap-2">
          <svg class="zena-notify-bell-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
          <span class="pdp-atc-text">SOLD OUT</span>
          <span class="zena-notify-pill ms-2">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg> Notify Me
          </span>
        </span>`
      if (this.buyBtn) {
        this.buyBtn.style.display = 'none'
      }
    }
  }

  async fetchNewDocument (selectedVariant) {
    try {
      const response = await fetch(`${this.form.dataset.productUrl}?variant=${selectedVariant.id}`)
      if (response.ok) {
        const text = await response.text()
        const newDocument = new DOMParser().parseFromString(text, 'text/html')
        window.dispatchEvent(new CustomEvent('kt.product.variant.change.newDoc', {
          detail: newDocument
        }))
      }
    } catch (e) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('warn', 'Failed to fetch new variant document', e)
      }
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    if (this.atcBtn) {
      this.atcBtn.classList.add('loading')
      this.atcBtn.disabled = true
      this.atcBtn.setAttribute('aria-busy', 'true')
    }

    try {
      const formData = new FormData(this.form)
      const cartContainer = document.querySelector('cart-container')
      if (cartContainer && typeof cartContainer.add === 'function') {
        await cartContainer.add(formData)
      } else {
        await fetch(`${window.Shopify?.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          body: formData
        })
        const offcanvasEl = document.getElementById('offcanvas-cart')
        if (offcanvasEl && window.bootstrap && window.bootstrap.Offcanvas) {
          const bsOffcanvas = window.bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl)
          bsOffcanvas.show()
        } else {
          window.location.href = '/?cart=true'
        }
      }
    } catch (err) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('error', 'Add to cart submission error', err)
      }
    } finally {
      if (this.atcBtn) {
        this.atcBtn.classList.remove('loading')
        this.atcBtn.disabled = false
        this.atcBtn.setAttribute('aria-busy', 'false')
      }
    }
  }

  onClickBuyBtn (event) {
    if (event) {
      event.preventDefault()
    }
    if (typeof window.handleGoKwikBuyNow === 'function') {
      window.handleGoKwikBuyNow(this.buyBtn)
      return
    }
    try {
      this.buyBtn?.classList.add('loading')
      if (this.buyBtn) this.buyBtn.disabled = true
      const variantId = this.form.querySelector('[name="id"]')?.value
      const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
      if (variantId) {
        window.location.href = `/cart/${variantId}:${qty}`
      }
    } catch (err) {
      this.buyBtn?.classList.remove('loading')
      if (this.buyBtn) this.buyBtn.disabled = false
    }
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage

    if (variantImage.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImage)
    }
  }

  handleOutOfStockOptions () {
    if (!this.form.dataset.variants) return

    let variants = []
    try {
      variants = JSON.parse(this.form.dataset.variants || '[]') || []
    } catch (e) {
      return
    }

    const selectedOption1 = this.form.querySelector('input[data-option-position="1"]:checked')?.value
    const selectedOption2 = this.form.querySelector('input[data-option-position="2"]:checked')?.value
    const selectedOption3 = this.form.querySelector('input[data-option-position="3"]:checked')?.value

    this.form.querySelectorAll('input[data-option-position="1"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (selectedOption3) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2 && variant.option3 === selectedOption3) return variant
        } else if (selectedOption2) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2) return variant
        } else {
          if (variant.option1 === input.value) return variant
        }
        return null
      })

      if (correspondingVariant?.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })

    this.form.querySelectorAll('input[data-option-position="2"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (selectedOption3) {
          if (variant.option2 === input.value && variant.option1 === selectedOption1 && variant.option3 === selectedOption3) return variant
        } else {
          if (variant.option2 === input.value && variant.option1 === selectedOption1) return variant
        }
        return null
      })

      if (correspondingVariant?.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })

    this.form.querySelectorAll('input[data-option-position="3"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (variant.option3 === input.value && variant.option1 === selectedOption1 && variant.option2 === selectedOption2) return variant
        return null
      })

      if (correspondingVariant?.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide) || 0

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    if (this.main) {
      if (this.main.params.loop) {
        this.main.slideToLoop(initialSlide, 0)
      } else {
        this.main.slideTo(initialSlide, 0)
      }
    }

    this.listenVariantChange()
    this.initFullScreenGallery()
    this.initThumbnailInteractions()
  }

  initThumbnailInteractions () {
    const thumbSlides = this.querySelectorAll('.swiper-thumbs .swiper-slide')
    thumbSlides.forEach((slide, index) => {
      slide.addEventListener('click', (e) => {
        e.preventDefault()
        if (this.main) {
          if (this.main.params.loop) {
            this.main.slideToLoop(index)
          } else {
            this.main.slideTo(index)
          }
          this.updateActiveThumb(index)
        }
      })
      slide.addEventListener('mouseenter', () => {
        if (window.matchMedia('(min-width: 992px)').matches && this.main) {
          if (this.main.params.loop) {
            this.main.slideToLoop(index)
          } else {
            this.main.slideTo(index)
          }
          this.updateActiveThumb(index)
        }
      })
    })

    if (this.main) {
      this.main.on('slideChange', () => {
        const activeIdx = typeof this.main.realIndex !== 'undefined' ? this.main.realIndex : this.main.activeIndex
        this.updateActiveThumb(activeIdx)
        if (this.thumbs && typeof this.thumbs.slideTo === 'function') {
          this.thumbs.slideTo(activeIdx)
        }
      })
    }
  }

  updateActiveThumb (index) {
    const thumbContainer = this.querySelector('.cc-swiper-thumbs, .swiper-thumbs')
    const thumbSlides = this.querySelectorAll('.swiper-thumbs .swiper-slide')
    thumbSlides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add('swiper-slide-thumb-active', 'is-active')
        if (thumbContainer && typeof slide.offsetTop !== 'undefined') {
          const targetTop = slide.offsetTop - (thumbContainer.clientHeight / 2) + (slide.clientHeight / 2)
          thumbContainer.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth'
          })
        }
      } else {
        slide.classList.remove('swiper-slide-thumb-active', 'is-active')
      }
    })
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: 'auto',
      spaceBetween: 12,
      freeMode: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true
      }
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      effect: this.dataset.effect || 'slide',
      speed: 400,
      loop: true,
      grabCursor: true,
      simulateTouch: true,
      touchRatio: 1.2,
      touchAngle: 45,
      resistanceRatio: 0.85,
      followFinger: true,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
        thresholdDelta: 15,
        releaseOnEdges: true
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true
      },
      pagination: {
        type: this.dataset.paginationType,
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      breakpoints: {
        0: { slidesPerView: 1 },
        600: { slidesPerView: 1 }
      },
      on: {
        slideChange: this.onSlideChange.bind(this)
      }
    }
    return options
  }

  onSlideChange () {
    this.querySelectorAll('video').forEach(video => video.pause())

    setTimeout(() => {
      if (this.querySelector('.swiper-main .swiper-slide-active video')) {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'true')
      } else {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'false')
      }
    }, 500)
  }

  setStickyPosition () {
    // Disabled dynamic scroll listener to keep gallery rock-solid and static
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant?.featured_media && this.main) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (this.dataset.fullScreen === 'false') return

    const initLightbox = () => {
      if (typeof window.GLightbox === 'function') {
        window.GLightbox({
          selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
          loop: true,
          openEffect: 'zoom',
          closeEffect: 'fade',
          slideEffect: this.dataset.fullScreenEffect
        })
      }
    }

    if (typeof window.GLightbox === 'function') {
      initLightbox()
    } else {
      const vendorScript = document.querySelector('script[src*="vendor-glightbox.min"]')
      if (vendorScript) {
        vendorScript.addEventListener('load', initLightbox, { once: true })
      }
    }
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

class QtySwitcher extends HTMLElement {
  constructor () {
    super()

    // If inside cart, cart.js delegates quantity changes to prevent competing double-increments
    if (this.closest('#offcanvas-cart, .offcanvas-cart, .cart-item, #cart-items, .cart-container, cart-container, .cart-form, form[action*="/cart"]')) {
      return
    }

    this.qtyInput = this.querySelector('input')
    this.minusBtn = this.querySelector('button.btn-qty-minus')
    this.plusBtn = this.querySelector('button.btn-qty-plus')

    if (!this.qtyInput || !this.minusBtn || !this.plusBtn) return

    this.qtyInput.addEventListener('change', () => this.onQtyInputChange())
    this.minusBtn.addEventListener('click', () => this.onMinusBtnClick())
    this.plusBtn.addEventListener('click', () => this.onPlusBtnClick())

    window.addEventListener('kt.product.variant.change.newDoc', (event) => this.onVariantChange(event))
  }

  onQtyInputChange () {
    this.validateButtons()
  }

  onMinusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) - 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  onPlusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) + 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  validateButtons () {
    if (this.qtyInput.getAttribute('min') && Number(this.qtyInput.value) <= Number(this.qtyInput.getAttribute('min'))) {
      this.minusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('min')
    } else {
      this.minusBtn.disabled = false
    }
    if (this.qtyInput.getAttribute('max') && Number(this.qtyInput.value) >= Number(this.qtyInput.getAttribute('max'))) {
      this.plusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('max')
    } else {
      this.plusBtn.disabled = false
    }
  }

  onVariantChange (event) {
    if (!this.closest('.product-block-atc')) return

    const newDoc = event.detail
    const newQtyInput = newDoc.querySelector('.product-block-atc qty-switcher input')

    this.qtyInput.setAttribute('max', newQtyInput.getAttribute('max'))
    this.validateButtons()
  }
}
customElements.define('qty-switcher', QtySwitcher)

class ProductStockIndicator extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    this.animateProgressBar()
    this.handleVariantChange()
  }

  animateProgressBar () {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      this.querySelector('.product-block-stock-indicator-inner').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('[id*="product-block-stock-indicator"]'))
      this.animateProgressBar()
    })
  }
}
customElements.define('product-stock-indicator', ProductStockIndicator)

class ProductQuantityBreaks extends HTMLElement {
  constructor () {
    super()

    this.handleInputChange()
    this.handleVariantChange()
  }

  handleInputChange (btn) {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const qtyField = input.closest('form').querySelector('[name="quantity"]')
        qtyField.value = input.value
        qtyField.dispatchEvent(new CustomEvent('change'))

        const atcBtn = this.closest('form').querySelector('.btn-atc')

        setTimeout(() => {
          atcBtn.classList.add('animate__animated', 'animate__shakeX')
        }, 250)

        setTimeout(() => {
          atcBtn.classList.remove('animate__animated', 'animate__shakeX')
        }, 1500)
      })
    })
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      this.querySelector('.product-block-quantity-breaks-list').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('product-quantity-breaks'))
      this.handleInputChange()
    })
  }
}
customElements.define('product-quantity-breaks', ProductQuantityBreaks)

class ProductQuickButton extends HTMLElement {
  constructor () {
    super()

    this.btn = this.querySelector('.btn-quick-view')
    this.modalCustomElem = document.querySelector('product-quick-view-modal')

    if (this.btn) {
      this.btn.addEventListener('click', async (event) => this.fetchProductData(event))
    }
  }

  async fetchProductData (event) {
    event.preventDefault()
    if (!this.btn || !this.btn.dataset.productUrl) return

    this.setLoading(true)

    try {
      const response = await fetch(this.btn.dataset.productUrl)
      if (response.ok) {
        const text = await response.text()
        const newDoc = new DOMParser().parseFromString(text, 'text/html')
        const modal = document.querySelector('product-quick-view-modal')
        if (modal && typeof modal.showModal === 'function') {
          modal.showModal(newDoc)
        }
      }
    } catch (err) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('error', 'Quick view fetch failed', err)
      }
    } finally {
      this.setLoading(false)
    }
  }

  setLoading (isLoading) {
    if (!this.btn) return
    if (isLoading) {
      this.btn.classList.add('loading')
      this.btn.disabled = true
      this.btn.setAttribute('aria-busy', 'true')
    } else {
      this.btn.classList.remove('loading')
      this.btn.disabled = false
      this.btn.removeAttribute('aria-busy')
    }
  }
}
customElements.define('product-quick-view-button', ProductQuickButton)

class ProductQuickViewModal extends HTMLElement {
  constructor () {
    super()

    this.modalElem = document.querySelector('#quick-view-modal')
    if (!this.modalElem) return

    this.modalElem.addEventListener('hidden.bs.modal', () => {
      const productEl = this.modalElem.querySelector('.product')
      if (productEl) {
        productEl.innerHTML = '<div data-quick-view-contents></div>'
      }
    })

    window.addEventListener('kt.cart.added', () => {
      if (window.bootstrap?.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(this.modalElem).hide()
      }
    })
  }

  async showModal (newDoc) {
    if (!this.modalElem || !newDoc) return

    const newContents = newDoc.querySelector('.product-main [data-quick-view-contents]')
    const placeholder = this.modalElem.querySelector('[data-quick-view-contents]')
    if (placeholder && newContents) {
      placeholder.replaceWith(newContents)
    }

    const title = this.modalElem.querySelector('.product-title')
    if (title) title.setAttribute('id', 'quick-view-modal-label')

    this.modalElem.querySelectorAll('.collapse-wrapper').forEach(collapseWrapper => {
      collapseWrapper.querySelector('[data-bs-toggle="collapse"]')?.setAttribute('aria-expanded', 'false')
      collapseWrapper.querySelector('.collapse')?.classList.remove('show')
    })

    this.modalElem.querySelectorAll('.product-options [name*="option"]').forEach(elem => {
      elem.setAttribute('name', `${elem.name}-qv`)
      elem.setAttribute('id', `${elem.id}-qv`)
    })

    this.modalElem.querySelectorAll('.product-options label').forEach(label => {
      label.setAttribute('for', `${label.getAttribute('for')}-qv`)
    })

    this.modalElem.querySelectorAll('.collapse').forEach(elem => {
      elem.addEventListener('shown.bs.collapse', () => {
        elem.closest('.product-block-collapse')?.scrollIntoView({ behavior: 'smooth' })
      })
    })

    if (window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(this.modalElem).show()
    }
    window.dispatchEvent(new CustomEvent('kt.product.quick_view.modal_shown'))
  }
}
customElements.define('product-quick-view-modal', ProductQuickViewModal)

class ProductDropupSelector extends HTMLElement {
  constructor () {
    super()

    this.productCard = this.closest('.product-card')
    this.dropdownBtn = this.querySelector('.dropdown-toggle')

    this.querySelector('.dropdown-menu').style.maxHeight =
      this.productCard.offsetHeight - this.dropdownBtn.offsetHeight - 2 + 'px'

    this.addEventListener('show.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px').matches

      if (isMobile) {
        event.preventDefault()
        this.initOffcanvas()
      } else {
        this.productCard.querySelector('.product-card-img-wrapper').style.opacity = '.2'
      }
    })
    this.addEventListener('hide.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px').matches

      if (isMobile) {
        event.preventDefault()
        this.removeOffcanvas()
      } else {
        this.productCard.querySelector('.product-card-img-wrapper').style.opacity = '1'
      }
    })

    this.querySelectorAll('.dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  async addToCart (btn) {
    if (window.bootstrap?.Dropdown) {
      window.bootstrap.Dropdown.getOrCreateInstance(this).hide()
    }

    this.dropdownBtn.classList.add('loading')
    this.dropdownBtn.disabled = true
    this.dropdownBtn.setAttribute('aria-busy', 'true')

    try {
      const items = [{
        id: Number(btn.dataset.variantId),
        quantity: 1
      }]

      const cartContainer = document.querySelector('cart-container')
      if (cartContainer && typeof cartContainer.add === 'function') {
        await cartContainer.add({ items })
      }
    } catch (err) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('error', 'Dropup addToCart failed', err)
      }
    } finally {
      this.dropdownBtn.classList.remove('loading')
      this.dropdownBtn.disabled = false
      this.dropdownBtn.setAttribute('aria-busy', 'false')
    }
  }

  initOffcanvas () {
    const html = `
      <div id="product-dropup-select-offcanvas" class="offcanvas offcanvas-bottom" tabindex="-1" aria-labelledby="product-dropup-select-offcanvas-title">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="product-dropup-select-offcanvas-title">
            ${this.dropdownBtn.textContent}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" class="svg-icon-x" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="offcanvas-body p-0">
          <ul class="dropdown-menu">
            ${this.querySelector('.dropdown-menu').innerHTML}
          </ul>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML('beforeend', html)

    window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').show()

    document.querySelectorAll('#product-dropup-select-offcanvas .dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => {
        this.addToCart(btn)
        window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').hide()
      })
    })

    document.querySelector('#product-dropup-select-offcanvas').addEventListener('hidden.bs.offcanvas', () => {
      document.querySelector('#product-dropup-select-offcanvas').remove()
    })
  }
}
customElements.define('product-dropup-selector', ProductDropupSelector)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onCheckboxChange(checkbox))
    })

    this.querySelectorAll('select[name="variant-id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.querySelectorAll('.btn-atc').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })

    // Dynamic fallback: if no cross-sell products rendered, fetch complementary recommendations
    this.loadDynamicFallback()
  }

  async loadDynamicFallback () {
    const productCards = this.querySelectorAll('.product-card, .product-block-cross-sells-list-item')
    // Only trigger fallback if section exists but has very few products (just the current one or none from metafields)
    if (productCards.length > 1) return

    const productId = this.closest('[data-product-id]')?.dataset?.productId
      || document.querySelector('[data-product-id]')?.dataset?.productId
    if (!productId) return

    try {
      const res = await fetch(`${window.Shopify.routes?.root_url || '/'}recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`)
      if (!res.ok) return
      const data = await res.json()
      if (!data.products || data.products.length < 1) return

      // Dispatch event so the product page knows complementary recs are available
      window.dispatchEvent(new CustomEvent('zena.crosssells.dynamic', {
        detail: { products: data.products, sourceElement: this }
      }))
    } catch (e) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('warn', 'Dynamic cross-sells fallback failed', e)
      }
    }
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex]?.dataset?.variantImage

    if (variantImage && variantImage.length) {
      const img = select.closest('.product-card')?.querySelector('.product-card-img-wrapper img')
      if (img) img.setAttribute('src', variantImage)
    }

    this.updateTotalPrice()
  }

  onCheckboxChange (checkbox) {
    if (checkbox.checked) {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'true')
    } else {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'false')
    }
    this.updateTotalPrice()
  }

  updateTotalPrice () {
    let totalPrice = 0
    let totalComparePrice = 0

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const inputHidden = checkbox.closest('.product-card').querySelector('input[name="variant-id"][type="hidden"]')
        if (inputHidden) {
          totalPrice += Number(inputHidden.dataset.price)
          totalComparePrice += Number(inputHidden.dataset.compareAtPrice)
        }

        const select = checkbox.closest('.product-card').querySelector('select[name="variant-id"]')
        if (select) {
          totalPrice += Number(select[select.selectedIndex].dataset.price)
          totalComparePrice += Number(select[select.selectedIndex].dataset.compareAtPrice)
        }
      }
    })

    this.querySelectorAll('[data-total-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalPrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-savings]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice - totalPrice)}`.replace('.00', '')
    })

    if (totalPrice === 0) {
      this.querySelector('.btn-atc').disabled = true
    } else {
      this.querySelector('.btn-atc').disabled = false
    }

    if (totalComparePrice > totalPrice) {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').removeAttribute('hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').removeAttribute('hidden')
      })
    } else {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').setAttribute('hidden', 'hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').setAttribute('hidden', 'hidden')
      })
    }
  }

  async addToCart (atcBtn) {
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    try {
      const items = []

      this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        if (checkbox.checked) {
          const id = Number(checkbox.closest('.product-card')?.querySelector('[name="variant-id"]')?.value)
          if (id) {
            items.push({ id, quantity: 1 })
          }
        }
      })

      if (items.length > 0) {
        const cartContainer = document.querySelector('cart-container')
        if (cartContainer && typeof cartContainer.add === 'function') {
          await cartContainer.add({ items })
        }
      }
    } catch (err) {
      if (typeof window.zenaLog === 'function') {
        window.zenaLog('error', 'CrossSells addToCart failed', err)
      }
    } finally {
      atcBtn.style.width = ''
      atcBtn.classList.remove('loading')
      atcBtn.disabled = false
      atcBtn.setAttribute('aria-busy', 'false')
    }
  }
}
customElements.define('cross-sells', CrossSells)

class ProductCard extends HTMLElement {
  constructor () {
    super()

    this.initCarousel()
  }

  initCarousel () {
    const carouselEl = this.querySelector('.product-card-img-wrapper .carousel')
    if (!carouselEl) return

    const items = carouselEl.querySelectorAll('.carousel-item')
    if (items.length < 2) return

    // Disable any auto-play — the image only changes on interaction
    this.carousel = window.bootstrap.Carousel.getOrCreateInstance(carouselEl, {
      interval: false,
      ride: false,
      pause: false,
      touch: false
    })

    const carouselInner = carouselEl.querySelector('.carousel-inner')

    if (window.matchMedia('(pointer: coarse)').matches) {
      // Mobile: show 2nd image on touch; revert every other card to its 1st image
      carouselInner.addEventListener('touchstart', () => {
        document.querySelectorAll('product-card').forEach((card) => {
          if (card !== this && typeof card.resetCarousel === 'function') card.resetCarousel()
        })
        this.showSecond()
      }, { passive: true })
    } else {
      // Desktop: 2nd image on hover, back to 1st on leave
      carouselInner.addEventListener('mouseenter', () => this.showSecond())
      carouselInner.addEventListener('mouseleave', () => this.resetCarousel())
    }
  }

  showSecond () {
    if (this.carousel) this.carousel.to(1)
  }

  resetCarousel () {
    if (this.carousel) this.carousel.to(0)
  }
}
customElements.define('product-card', ProductCard)

class FileUploadPersonalizationField extends HTMLElement {
  constructor () {
    super()

    this.loadStyleScripts()
    window.addEventListener('kt.product.quick_view.modal_shown', () => this.init())
  }

  loadStyleScripts () {
    if (window.FilePond) {
      this.init()
      return
    }

    if (!document.querySelector('link[href*="filepond.min.css"]')) {
      const style1 = document.createElement('link')
      style1.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.css')
      style1.setAttribute('rel', 'stylesheet')
      document.head.appendChild(style1)
    }

    if (!document.querySelector('link[href*="filepond-plugin-image-preview.min.css"]')) {
      const style2 = document.createElement('link')
      style2.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.css')
      style2.setAttribute('rel', 'stylesheet')
      document.head.appendChild(style2)
    }

    if (!document.querySelector('script[src*="filepond.min.js"]')) {
      const script1 = document.createElement('script')
      script1.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.js')
      script1.setAttribute('defer', 'defer')
      document.head.appendChild(script1)

      const script2 = document.createElement('script')
      script2.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.js')
      script2.setAttribute('defer', 'defer')
      script2.onload = () => this.init()
      document.head.appendChild(script2)
    }
  }

  async init () {
    if (!window.FilePond) return

    if (window.FilePondPluginImagePreview) {
      window.FilePond.registerPlugin(window.FilePondPluginImagePreview)
    }
    const inputElem = this.querySelector('input[type="file"]')
    const labelElem = this.querySelector('[data-label-text]')

    if (inputElem && !inputElem.classList.contains('filepond--root')) {
      window.FilePond.create(inputElem, {
        storeAsFile: true,
        labelIdle: labelElem?.innerHTML || 'Drag & Drop your file or <span class="filepond--label-action">Browse</span>'
      })
    }
  }
}
customElements.define('file-upload-personalization-field', FileUploadPersonalizationField)

// Product block collapses
if (window.matchMedia('(max-width: 1199px').matches) {
  document.querySelectorAll('.product-block-collapse').forEach(elem => {
    elem.querySelector('.collapse').addEventListener('shown.bs.collapse', () => {
      elem.scrollIntoView({ behavior: 'smooth' })
    })
  })
}

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
